"use client";

import { Button } from "@ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { CheckIcon, ChevronDownIcon, CopyIcon, FileTextIcon, SparklesIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import {
  AI_ASSISTANTS,
  buildReportAskPrompt,
  type ReportType,
} from "../lib/build-report-markdown";

export interface ReportAIActionsProps {
  /** Absolute URL serving the report as Markdown. */
  markdownUrl: string;
  reportType: ReportType;
  /** Human label for the subject — a brand name or domain. */
  subjectLabel: string;
  /** Stack buttons vertically (matches ShareButtons' vertical layout). */
  vertical?: boolean;
}

/**
 * "Chat with this report" actions: a single dropdown to open the report in an AI
 * assistant (prefilled prompt linking to the Markdown URL), plus copy / view the
 * Markdown. Copies by fetching the Markdown URL (the report isn't already in
 * props).
 */
export function ReportAIActions({
  markdownUrl,
  reportType,
  subjectLabel,
  vertical = false,
}: ReportAIActionsProps) {
  const t = useTranslations("marketing.reports.aiActions");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const res = await fetch(markdownUrl);
      if (!res.ok) throw new Error(`Failed to fetch markdown: ${res.status}`);
      const text = await res.text();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy report markdown:", err);
    }
  };

  const prompt = buildReportAskPrompt({ reportType, subjectLabel, markdownUrl });
  const fullWidth = vertical ? "w-full justify-start" : "";

  return (
    <div className={vertical ? "space-y-2" : "flex flex-wrap items-center gap-2"}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={fullWidth}>
            <SparklesIcon className="mr-2 size-4" />
            {t("chatWith")}
            <ChevronDownIcon className="ml-2 size-4 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={vertical ? "start" : "end"} className="w-48">
          {AI_ASSISTANTS.map((assistant) => (
            <DropdownMenuItem key={assistant.id} asChild>
              <a
                href={assistant.urlFor(prompt)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={assistant.logo}
                  alt=""
                  aria-hidden
                  className="mr-2 size-4"
                />
                {t("ask", { tool: assistant.label })}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="secondary"
        className={fullWidth}
        onClick={handleCopy}
        data-testid="copy-report-markdown"
      >
        {copied ? (
          <CheckIcon className="mr-2 size-4" />
        ) : (
          <CopyIcon className="mr-2 size-4" />
        )}
        {copied ? t("copied") : t("copy")}
      </Button>

      <Button variant="outline" className={fullWidth} asChild>
        <a href={markdownUrl} target="_blank" rel="noopener noreferrer">
          <FileTextIcon className="mr-2 size-4" />
          {t("viewMarkdown")}
        </a>
      </Button>
    </div>
  );
}
